import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useVideo } from '../../context/VideoContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiX, FiPlus, FiTrash2, FiStar, FiDollarSign, FiHash } = FiIcons;

const CompanyForm = ({ company, onClose }) => {
  const { addCompany, updateCompany } = useVideo();
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    clutch_rating: 1,
    clutch_review_count: '',
    overall_rating: 1,
    portfolio_size: '',
    active_since: 2015,
    location: '',
    avg_price: '',
    speciality: '',
    notable_people: [],
    pros: [],
    cons: [],
    why_it_works: '',
  });

  useEffect(() => {
    if (company) {
      setFormData({
        ...company,
        clutch_review_count: company.clutch_review_count ? String(company.clutch_review_count) : '',
        portfolio_size: company.portfolio_size ? String(company.portfolio_size) : '',
        why_it_works: company.why_it_works || '',
      });
    }
  }, [company]);

  const handleAddPerson = () => {
    setFormData({
      ...formData,
      notable_people: [...formData.notable_people, { name: '', position: '', photo_url: '', linkedin: '' }]
    });
  };

  const handleAddBullet = (field) => {
    setFormData({ ...formData, [field]: [...(formData[field] || []), ''] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      clutch_rating: Math.min(5, Math.max(1, parseFloat(formData.clutch_rating) || 1)),
      clutch_review_count: formData.clutch_review_count,
      portfolio_size: formData.portfolio_size,
      overall_rating: Math.min(5, Math.max(1, parseFloat(formData.overall_rating) || 1)),
      why_it_works: formData.why_it_works || null,
    };
    if (company) await updateCompany(company.id, data);
    else await addCompany(data);
    onClose();
  };

  const inputClasses = "w-full px-4 py-3 bg-zinc-900/50 border border-white/10 rounded-2xl text-white placeholder-white/20 focus:border-orange-500 transition-all outline-none";

  return (
    <motion.div
      className="glass-card p-8 border-white/5"
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      <div className="flex justify-between items-center mb-8 pb-4 border-b border-white/5">
        <h3 className="text-2xl font-bold text-white flex items-center gap-3">
          <SafeIcon icon={FiPlus} className="text-orange-500" />
          {company ? 'Edit Company' : 'Add Production House'}
        </h3>
        <button onClick={onClose} className="text-white/40 hover:text-white">
          <SafeIcon icon={FiX} className="text-2xl" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          <div className="space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Company Name</label>
            <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className={inputClasses} required />
          </div>

          <div className="space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Logo URL</label>
            <input type="url" value={formData.logo_url} onChange={e => setFormData({...formData, logo_url: e.target.value})} className={inputClasses} />
          </div>

          <div className="space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest flex items-center gap-2">
              Clutch Rating <span className="text-white/20">|</span> Total Reviews
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <SafeIcon icon={FiStar} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                <input type="number" step="0.1" min="1" max="5" value={formData.clutch_rating} onChange={e => setFormData({...formData, clutch_rating: e.target.value})} onWheel={e => e.target.blur()} className={`${inputClasses} pl-10`} placeholder="4.9" />
              </div>
              <div className="flex-1 relative">
                <SafeIcon icon={FiHash} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                <input type="text" value={formData.clutch_review_count} onChange={e => setFormData({...formData, clutch_review_count: e.target.value})} className={`${inputClasses} pl-10`} placeholder="e.g. 120 or 1k+" />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Overall Rating (1-5)</label>
            <input type="number" step="0.1" min="1" max="5" value={formData.overall_rating} onChange={e => setFormData({...formData, overall_rating: e.target.value})} onWheel={e => e.target.blur()} className={inputClasses} />
          </div>

          <div className="space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">SaaS Projects Size</label>
            <input type="text" value={formData.portfolio_size} onChange={e => setFormData({...formData, portfolio_size: e.target.value})} className={inputClasses} placeholder="e.g. 50+" />
          </div>

          <div className="space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Active Since</label>
            <input type="number" value={formData.active_since} onChange={e => setFormData({...formData, active_since: e.target.value})} onWheel={e => e.target.blur()} className={inputClasses} />
          </div>

          <div className="space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Location</label>
            <input type="text" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} className={inputClasses} />
          </div>

          <div className="space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Avg Price Range</label>
            <input type="text" value={formData.avg_price} onChange={e => setFormData({...formData, avg_price: e.target.value})} className={inputClasses} placeholder="e.g. $5k - $15k" />
          </div>

          <div className="md:col-span-2 space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Speciality</label>
            <input type="text" value={formData.speciality} onChange={e => setFormData({...formData, speciality: e.target.value})} className={inputClasses} placeholder="e.g. High-end 3D Animation" />
          </div>

          {/* Why It Works — full width textarea */}
          <div className="md:col-span-2 space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Why This Company Works</label>
            <p className="text-white/20 text-xs">One bullet per line. Each line becomes a bullet in the card hover overlay.</p>
            <textarea
              value={formData.why_it_works}
              onChange={e => setFormData({...formData, why_it_works: e.target.value})}
              className={`${inputClasses} h-32 resize-none`}
              placeholder={"- Specializes in SaaS product demos\n- Fast turnaround, typically 2–3 weeks\n- Strong motion design portfolio"}
            />
          </div>
        </div>

        {/* Notable People */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Notable People</label>
            <button type="button" onClick={handleAddPerson} className="text-orange-500 text-xs font-bold flex items-center gap-1">
              <SafeIcon icon={FiPlus} /> Add Member
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {formData.notable_people?.map((person, idx) => (
              <div key={idx} className="flex flex-col md:flex-row gap-3 items-start bg-white/5 p-4 rounded-3xl border border-white/5 relative">
                <input type="text" placeholder="Name" value={person.name} onChange={e => { const p = [...formData.notable_people]; p[idx].name = e.target.value; setFormData({...formData, notable_people: p}); }} className={inputClasses} />
                <input type="text" placeholder="Position (e.g. Founder)" value={person.position} onChange={e => { const p = [...formData.notable_people]; p[idx].position = e.target.value; setFormData({...formData, notable_people: p}); }} className={inputClasses} />
                <input type="url" placeholder="Photo URL" value={person.photo_url} onChange={e => { const p = [...formData.notable_people]; p[idx].photo_url = e.target.value; setFormData({...formData, notable_people: p}); }} className={inputClasses} />
                <input type="url" placeholder="LinkedIn" value={person.linkedin} onChange={e => { const p = [...formData.notable_people]; p[idx].linkedin = e.target.value; setFormData({...formData, notable_people: p}); }} className={inputClasses} />
                <button type="button" onClick={() => setFormData({...formData, notable_people: formData.notable_people.filter((_, i) => i !== idx)})} className="p-3 text-red-500 hover:bg-red-500/10 rounded-xl transition-all">
                  <SafeIcon icon={FiTrash2} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Pros & Cons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <BulletSection
            label="Pros"
            items={formData.pros || []}
            onAdd={() => handleAddBullet('pros')}
            onUpdate={(idx, val) => { const n = [...formData.pros]; n[idx] = val; setFormData({...formData, pros: n}); }}
            onRemove={(idx) => setFormData({...formData, pros: formData.pros.filter((_, i) => i !== idx)})}
          />
          <BulletSection
            label="Cons"
            items={formData.cons || []}
            onAdd={() => handleAddBullet('cons')}
            onUpdate={(idx, val) => { const n = [...formData.cons]; n[idx] = val; setFormData({...formData, cons: n}); }}
            onRemove={(idx) => setFormData({...formData, cons: formData.cons.filter((_, i) => i !== idx)})}
          />
        </div>

        <div className="flex justify-end gap-4 pt-8 border-t border-white/5">
          <button type="button" onClick={onClose} className="btn-secondary">Discard</button>
          <button type="submit" className="btn-primary flex items-center gap-2 px-8">
            <SafeIcon icon={FiSave} />
            {company ? 'Update Profile' : 'Add to Directory'}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

const BulletSection = ({ label, items, onAdd, onUpdate, onRemove }) => (
  <div className="space-y-4">
    <div className="flex justify-between items-center">
      <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">{label}</label>
      <button type="button" onClick={onAdd} className="text-orange-500 text-xs font-bold flex items-center gap-1">
        <SafeIcon icon={FiPlus} /> Add
      </button>
    </div>
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx} className="flex gap-2">
          <input type="text" value={item} onChange={e => onUpdate(idx, e.target.value)} className="w-full px-4 py-2 bg-zinc-900/50 border border-white/10 rounded-xl text-white text-sm" />
          <button type="button" onClick={() => onRemove(idx)} className="text-red-400 p-2">
            <SafeIcon icon={FiTrash2} />
          </button>
        </div>
      ))}
    </div>
  </div>
);

export default CompanyForm;